import { useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users, TrendingUp, TrendingDown, Target, Inbox, UserCheck, Award, ArrowUpRight, Trophy, CalendarCheck } from "lucide-react";
import { Panel, StageBadge, Avatar, EmptyState, STAGE_META, relTime, slug } from "./crm";

function Kpi({ label, value, sub, icon: Icon, trend, accent = "text-slate-900", testid }) {
  return (
    <div className="min-w-[160px] snap-start rounded-xl border border-slate-200 bg-white p-4 sm:min-w-0 sm:p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-slate-500"><Icon className="h-4 w-4" /></span>
      </div>
      <div className={`font-mono-num mt-2.5 sm:mt-3 text-2xl sm:text-3xl font-bold tracking-tight ${accent}`}>{value}</div>
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
        {trend != null && (
          <span className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold ${trend >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(trend)}%
          </span>
        )}
        <span>{sub}</span>
      </div>
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="font-mono-num mt-0.5 text-slate-600">{payload[0].value} recruit{payload[0].value === 1 ? "" : "s"}</div>
    </div>
  );
}

function Funnel({ funnel, total }) {
  const max = Math.max(1, ...funnel.map((f) => f.count));
  return (
    <div className="space-y-3 p-5" data-testid="dashboard-funnel">
      {funnel.map((f) => {
        const m = STAGE_META[f.stage];
        const pct = total ? Math.round((f.count / total) * 100) : 0;
        return (
          <div key={f.stage} data-testid={`funnel-${slug(f.stage)}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 font-semibold text-slate-700"><span className={`h-2 w-2 rounded-full ${m.dot}`} />{f.stage}</span>
              <span className="font-mono-num text-slate-500"><span className="font-bold text-slate-900">{f.count}</span> · {pct}%</span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(f.count / max) * 100}%`, backgroundColor: m.hex }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Leaderboard({ rows }) {
  if (!rows.length) return <EmptyState icon={Trophy} title="No agents yet" hint="Add agents to the roster to see performance." />;
  return (
    <table className="w-full text-sm" data-testid="dashboard-leaderboard">
      <thead>
        <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
          <th className="px-5 py-2.5 text-left font-semibold">Agent</th>
          <th className="px-3 py-2.5 text-right font-semibold">Assigned</th>
          <th className="px-3 py-2.5 text-right font-semibold hidden sm:table-cell">Working</th>
          <th className="px-3 py-2.5 text-right font-semibold">Onboarded</th>
          <th className="px-5 py-2.5 text-right font-semibold">Close %</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={r.id} className="hover:bg-slate-50/70">
            <td className="px-5 py-3">
              <div className="flex items-center gap-3">
                <span className={`font-mono-num w-5 text-xs font-bold ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
                <Avatar name={r.name} size="sm" />
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{r.name}</div>
                  <div className="truncate text-[11px] text-slate-500">{r.active ? "In rotation" : "Paused"}</div>
                </div>
              </div>
            </td>
            <td className="font-mono-num px-3 py-3 text-right text-slate-700">{r.assigned}</td>
            <td className="font-mono-num px-3 py-3 text-right text-slate-700 hidden sm:table-cell">{r.contacted}</td>
            <td className="font-mono-num px-3 py-3 text-right font-bold text-emerald-700">{r.onboarded}</td>
            <td className="font-mono-num px-5 py-3 text-right text-slate-700">{r.assigned ? Math.round((r.onboarded / r.assigned) * 100) : 0}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QuizBreakdown({ data }) {
  const [idx, setIdx] = useState(0);
  if (!data.length) return <EmptyState icon={Target} title="No application data yet" hint="Answer distributions appear after the first recruit applies." />;
  const q = data[Math.min(idx, data.length - 1)];
  const total = q.options.reduce((s, o) => s + o.count, 0);
  return (
    <div className="p-5" data-testid="dashboard-quiz-breakdown">
      <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-1 px-1 no-scrollbar">
        {data.map((d, i) => (
          <button key={d.question} onClick={() => setIdx(i)} data-testid={`quiz-tab-${i}`} className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${i === idx ? "border-[#0b1528] bg-[#0b1528] text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {d.question.length > 34 ? d.question.slice(0, 32) + "…" : d.question}
          </button>
        ))}
      </div>
      <p className="text-xs font-semibold text-slate-700">{q.question}</p>
      <div className="mt-3 space-y-2.5">
        {q.options.map((o) => (
          <div key={o.answer}>
            <div className="flex items-center justify-between text-xs">
              <span className="truncate pr-3 text-slate-700">{o.answer}</span>
              <span className="font-mono-num text-slate-500"><span className="font-bold text-slate-900">{o.count}</span> · {Math.round((o.count / total) * 100)}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-[#0b1528]" style={{ width: `${(o.count / total) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardView({ stats, leads, onOpen, goTo, agentMode = false, userName }) {
  if (!stats) return <div className="animate-pulse space-y-4"><div className="h-28 rounded-xl bg-slate-200/60" /><div className="h-72 rounded-xl bg-slate-200/60" /></div>;
  const t = stats.totals;
  const recent = leads.slice(0, 6);
  const todayStr = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const greeting = agentMode ? `Welcome back${userName ? `, ${userName.split(" ")[0]}` : ""}` : "Dashboard";

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">{greeting}</h1>
          <p className="mt-1 text-sm text-slate-500">{todayStr} · {t.today} new {agentMode ? "for you " : ""}today</p>
        </div>
        <button onClick={() => goTo("pipeline")} data-testid="dashboard-open-pipeline" className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-300">
          Open pipeline <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>

      <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 no-scrollbar sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 xl:grid-cols-5" data-testid="kpi-strip">
        <Kpi label={agentMode ? "My Recruits" : "Total Recruits"} value={t.total} sub="all time" icon={Users} testid="kpi-total" />
        <Kpi label="This Week" value={t.this_week} sub="vs last week" trend={t.week_change} icon={TrendingUp} testid="kpi-week" />
        <Kpi label="Needs Contact" value={t.new_untouched} sub="in New stage" icon={Inbox} accent={t.new_untouched ? "text-sky-600" : "text-slate-900"} testid="kpi-new" />
        <Kpi label="Active Pipeline" value={t.active_pipeline} sub={`${t.contact_rate}% contact rate`} icon={Target} testid="kpi-pipeline" />
        <Kpi label="Interviews Booked" value={t.interviews_scheduled ?? 0} sub={`${t.booking_rate ?? 0}% of applicants · ${t.pending_scheduling ?? 0} awaiting`} icon={CalendarCheck} accent="text-cyan-600" testid="kpi-interviews" />
        <Kpi label="Onboarded" value={t.onboarded} sub={`${t.conversion_rate}% conversion`} icon={Award} accent="text-emerald-600" testid="kpi-onboarded" />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel title={agentMode ? "My recruits over time" : "Recruits over time"} subtitle="Daily applications, last 30 days" className="xl:col-span-2" testid="dashboard-timeline">
          <div className="h-56 sm:h-64 px-1 sm:px-2 pt-4 pb-2" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={200} initialDimension={{ width: 320, height: 200 }}>
              <AreaChart data={stats.timeline} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="breezeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00C6EF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00C6EF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ stroke: "#94a3b8", strokeDasharray: "3 3" }} />
                <Area type="monotone" dataKey="count" stroke="#0891b2" strokeWidth={2.5} fill="url(#breezeFill)" dot={false} activeDot={{ r: 4, fill: "#0891b2", stroke: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Pipeline funnel" subtitle={`${t.conversion_rate}% of recruits onboarded`}>
          <Funnel funnel={stats.funnel} total={t.total} />
        </Panel>
      </div>

      <div className={`grid gap-4 ${agentMode ? "" : "xl:grid-cols-3"}`}>
        {!agentMode && (
          <Panel title="Agent leaderboard" subtitle="Ranked by onboarded recruits" className="xl:col-span-2 overflow-hidden" action={<span className="text-xs text-slate-500"><span className="font-mono-num font-bold text-slate-900">{t.active_agents}</span>/{t.total_agents} in rotation</span>}>
            <div className="overflow-x-auto"><Leaderboard rows={stats.leaderboard} /></div>
          </Panel>
        )}
        <Panel title={agentMode ? "How my recruits answered" : "Application insights"} subtitle={agentMode ? "Know who you're calling before you dial" : "How recruits answered"}>
          <QuizBreakdown data={stats.quiz_breakdown} />
        </Panel>
      </div>

      <Panel title={agentMode ? "My recent recruits" : "Recent recruits"} subtitle="Latest submissions" action={<button onClick={() => goTo("recruits")} data-testid="dashboard-view-all" className="text-xs font-semibold text-sky-700 hover:underline">View all</button>} testid="dashboard-recent">
        {recent.length === 0 ? <EmptyState icon={UserCheck} title={agentMode ? "No recruits routed to you yet" : "No recruits yet"} hint={agentMode ? "You're in the rotation — new applications will appear here and you'll get an e-mail." : "Submissions from the funnel will show up here instantly."} /> : (
          <ul className="divide-y divide-slate-100">
            {recent.map((l) => (
              <li key={l.id}>
                <button onClick={() => onOpen(l.id)} data-testid={`recent-lead-${l.id}`} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-slate-50/70 transition-colors">
                  <Avatar name={l.full_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">{l.full_name}</div>
                    <div className="truncate text-xs text-slate-500">{l.email}</div>
                  </div>
                  <span className="hidden sm:block text-xs text-slate-500">{l.assigned_agent_name || "Unassigned"}</span>
                  <StageBadge stage={l.status} />
                  <span className="font-mono-num w-16 text-right text-[11px] text-slate-400">{relTime(l.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
